import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FiX,
  FiSearch,
  FiTrendingUp,
  FiEdit,
  FiRefreshCw,
  FiBarChart,
  FiPieChart,
  FiLayers,
  FiHexagon,
  FiCircle,
  FiSend,
  FiLock
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import VaultFractionalizationModal from './VaultFractionalizationModal';
import { formatEnumLabel } from '../../api/smartTokenizerMappers';
import AssetEditModal from './AssetEditModal';

const PortfolioContainer = styled.div`
  padding: 2rem;
  max-height: 90vh;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const PortfolioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PortfolioTitle = styled.h2`
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;

  .summary-value {
    font-size: 2rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .summary-label {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .summary-trend {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: #00ff88;
  }
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;

    &:focus {
      border-color: #4facfe;
      outline: none;
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0a0a0;
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  select {
    padding: 0.5rem 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;

    &:focus {
      border-color: #4facfe;
      outline: none;
    }

    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  button {
    padding: 0.5rem 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &.active {
      background: #4facfe;
      border-color: #4facfe;
    }
  }
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const AssetCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.1);
  }

  .asset-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .asset-info {
    flex: 1;
  }

  .asset-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
  }

  .asset-category {
    color: #4facfe;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .asset-description {
    color: #a0a0a0;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .asset-actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    padding: 0.5rem;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.1);
    color: #a0a0a0;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(79, 172, 254, 0.2);
      color: #4facfe;
    }
  }

  .asset-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin: 1rem 0;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
  }

  .metric {
    text-align: center;

    .metric-value {
      font-size: 1.1rem;
      font-weight: 600;
      color: #fff;
      margin-bottom: 0.25rem;
    }

    .metric-label {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }

  .tokenization-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 8px;
    margin-top: 1rem;
  }

  .token-details {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .token-icon {
      color: #4facfe;
      font-size: 1.2rem;
    }

    .token-info {
      .token-name {
        font-size: 0.9rem;
        font-weight: 600;
        color: #fff;
      }

      .token-address {
        font-size: 0.7rem;
        color: #a0a0a0;
        font-family: monospace;
      }
    }
  }

  .token-status {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    background: rgba(0, 255, 136, 0.1);
    color: #00ff88;
    border: 1px solid rgba(0, 255, 136, 0.3);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #a0a0a0;

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    color: #4facfe;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #fff;
  }

  .empty-description {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 0.25rem;

  button {
    padding: 0.5rem 1rem;
    border: none;
    background: none;
    color: #a0a0a0;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.3s ease;

    &.active {
      background: #4facfe;
      color: #fff;
    }

    &:hover:not(.active) {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  }
`;

const ASSET_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'SubmittedForLegalReview', label: 'Submitted for legal' },
  { value: 'UnderLegalReview', label: 'Under legal review' },
  { value: 'ApprovedForTokenization', label: 'Approved for tokenization' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'MetadataPinnedToIpfs', label: 'Metadata on IPFS' },
  { value: 'MintRequested', label: 'Mint requested' },
  { value: 'MintingInProgress', label: 'Minting in progress' },
  { value: 'Minted', label: 'Minted' },
  { value: 'VaultLockInProgress', label: 'Vault lock in progress' },
  { value: 'LockedInVault', label: 'Locked in vault' },
  { value: 'FractionalizationInProgress', label: 'Fractionalization in progress' },
  { value: 'Fractionalized', label: 'Fractionalized' }
];

const VAULT_FRACTIONAL_STATUSES = new Set([
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized'
]);

function AssetPortfolio({ onClose }) {
  const {
    assets,
    tokenizations,
    statistics,
    categories,
    loadAssets,
    getAssetById,
    updateAsset,
    submitAssetForLegalReview,
    loading
  } = useTokenization();

  const { assetClasses } = useAssetClass();
  const { isAdmin, isAuthorizedTokenizer } = useTokenIssuer();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [editingAsset, setEditingAsset] = useState(null);
  const [vaultAsset, setVaultAsset] = useState(null);

  const canUseVaultFlow = isAdmin || isAuthorizedTokenizer;

  const isErc721Asset = (asset) => {
    const ac = assetClasses.find((c) => c.id === asset.assetClassId);
    return ac?.tokenStandard === 'ERC721';
  };

  const showVaultFractionalAction = (asset) =>
    canUseVaultFlow && isErc721Asset(asset) && VAULT_FRACTIONAL_STATUSES.has(String(asset.status || ''));

  // Combine assets with their tokenization data
  const enrichedAssets = assets.map(asset => {
    const tokenization = tokenizations.find(t => t.assetId === asset.id);
    return {
      ...asset,
      tokenization
    };
  });

  // Filter assets
  const filteredAssets = enrichedAssets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || asset.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || String(asset.status) === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getTokenIcon = (tokenType) => {
    switch (tokenType) {
      case 'fungible': return <FiCircle />;
      case 'nonFungible': return <FiHexagon />;
      case 'fractionalNFT': return <FiLayers />;
      default: return <FiCircle />;
    }
  };

  const getStatusColor = (status) => {
    const s = String(status || '');
    if (s === 'Minted' || s === 'ApprovedForTokenization') return '#00ff88';
    if (s === 'Rejected') return '#ff6b6b';
    if (s.includes('Legal') || s === 'SubmittedForLegalReview') return '#ffc107';
    if (s.includes('Mint')) return '#4facfe';
    return '#a0a0a0';
  };

  return (
    <PortfolioContainer>
      <PortfolioHeader>
        <PortfolioTitle>
          <FiBarChart /> Asset Portfolio
        </PortfolioTitle>
        <CloseButton type="button" onClick={onClose}>
          <FiX />
        </CloseButton>
      </PortfolioHeader>

      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={() => loadAssets()}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            border: '1px solid rgba(79,172,254,0.4)',
            background: 'rgba(79,172,254,0.15)',
            color: '#9bd6ff',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem'
          }}
        >
          <FiRefreshCw />
          Refresh from API
        </button>
      </div>

      <SummaryGrid>
        <SummaryCard>
          <div className="summary-value">{statistics.totalAssets}</div>
          <div className="summary-label">Total Assets</div>
          <div className="summary-trend">
            <FiTrendingUp />
            +5 this month
          </div>
        </SummaryCard>

        <SummaryCard>
          <div className="summary-value">${(statistics.totalValue / 1000000).toFixed(1)}M</div>
          <div className="summary-label">Portfolio Value</div>
          <div className="summary-trend">
            <FiTrendingUp />
            +12.5% this month
          </div>
        </SummaryCard>

        <SummaryCard>
          <div className="summary-value">{statistics.activeTokenizations}</div>
          <div className="summary-label">Tokenized Assets</div>
          <div className="summary-trend">
            <FiTrendingUp />
            +3 this week
          </div>
        </SummaryCard>

        <SummaryCard>
          <div className="summary-value">{statistics.fractionalizedAssets}</div>
          <div className="summary-label">Fractionalized</div>
          <div className="summary-trend">
            <FiLayers />
            Advanced liquidity
          </div>
        </SummaryCard>
      </SummaryGrid>

      <ControlsSection>
        <SearchBox>
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search assets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>

        <FilterControls>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {Object.values(categories).map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {ASSET_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <ViewToggle>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FiPieChart />
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FiBarChart />
            </button>
          </ViewToggle>
        </FilterControls>
      </ControlsSection>

      {filteredAssets.length === 0 ? (
        <EmptyState>
          <FiBarChart className="empty-icon" />
          <div className="empty-title">No Assets Found</div>
          <div className="empty-description">
            {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start by creating your first asset to see it here'
            }
          </div>
        </EmptyState>
      ) : (
        <AssetGrid>
          <AnimatePresence>
            {filteredAssets.map((asset, index) => (
              <AssetCard
                key={asset.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="asset-header">
                  <div className="asset-info">
                    <div className="asset-name">{asset.name}</div>
                    <div className="asset-category">
                      {categories[asset.category]?.icon} {categories[asset.category]?.name}
                    </div>
                    <div className="asset-description">{asset.description}</div>
                  </div>
                  <div className="asset-actions">
                    <button
                      type="button"
                      className="action-btn"
                      title="Reload this asset"
                      onClick={async () => {
                        try {
                          await getAssetById(asset.id, true);
                          toast.success('Asset refreshed');
                        } catch (e) {
                          toast.error(e.message || 'Refresh failed');
                        }
                      }}
                    >
                      <FiRefreshCw />
                    </button>
                    <button
                      type="button"
                      className="action-btn"
                      title="Edit (PUT asset)"
                      onClick={() => setEditingAsset(asset)}
                    >
                      <FiEdit />
                    </button>
                    {(asset.status === 'Draft' || asset.status === 'Rejected') && (
                      <button
                        type="button"
                        className="action-btn"
                        title="Submit for legal review"
                        onClick={async () => {
                          try {
                            await submitAssetForLegalReview(asset.id);
                          } catch (e) {
                            /* toast in context */
                          }
                        }}
                      >
                        <FiSend />
                      </button>
                    )}
                    {showVaultFractionalAction(asset) && (
                      <button
                        type="button"
                        className="action-btn"
                        title="Vault lock & fractional ERC20 (API)"
                        onClick={() => setVaultAsset(asset)}
                      >
                        <FiLock />
                      </button>
                    )}
                  </div>
                </div>

                <div className="asset-metrics">
                  <div className="metric">
                    <div className="metric-value">
                      ${asset.valuationUSD?.toLocaleString() || '0'}
                    </div>
                    <div className="metric-label">Current Value</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value" style={{ color: getStatusColor(asset.status) }}>
                      {formatEnumLabel(asset.status)}
                    </div>
                    <div className="metric-label">Status</div>
                  </div>
                </div>

                {asset.tokenization && (
                  <div className="tokenization-info">
                    <div className="token-details">
                      <div className="token-icon">
                        {getTokenIcon(asset.tokenization.tokenType)}
                      </div>
                      <div className="token-info">
                        <div className="token-name">{asset.tokenization.symbol}</div>
                        <div className="token-address">
                          {asset.tokenization.contractAddress.substring(0, 10)}...
                        </div>
                      </div>
                    </div>
                    <div className="token-status">
                      {asset.tokenization.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                )}
              </AssetCard>
            ))}
          </AnimatePresence>
        </AssetGrid>
      )}

      <AnimatePresence>
        {editingAsset && (
          <AssetEditModal
            asset={editingAsset}
            onClose={() => setEditingAsset(null)}
            busy={loading}
            onSave={(id, updates) => updateAsset(id, updates)}
          />
        )}
        {vaultAsset && (
          <VaultFractionalizationModal
            asset={vaultAsset}
            onClose={() => setVaultAsset(null)}
            onUpdated={() => loadAssets()}
          />
        )}
      </AnimatePresence>
    </PortfolioContainer>
  );
}

export default AssetPortfolio; 